import React, { forwardRef } from 'react';

import { useDraggable } from '@dnd-kit/core';
import type { ButtonBaseProps } from '@mui/material';
import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import type { DragField } from 'src/components/drag-and-drop';
import { AlineTooltip } from 'src/components/generic';
import { cypressFlag } from 'src/helpers';
import { composeRefs } from 'src/helpers/compose-refs';
import { makeStyles } from 'tss-react/mui';

import { DragIcon } from './icons/DragIcon';

const useStyles = makeStyles<StyleProps>()((theme, { disabled }) => ({
  button: {
    '&:hover': {
      border: disabled
        ? `1px solid ${theme.appColors.rule}`
        : `1px solid ${theme.appColors.primary}`,
    },
    '&:hover svg': {
      visibility: 'visible',
    },
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    color: disabled ? theme.palette.action.disabled : theme.appColors.iconsType,
    // Explicitly set the height so the button doesn't "jump" on hover.
    height: 34,
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
    textDecoration: 'none',
    width: '100%',
  },
  dragIcon: {
    color: disabled ? theme.palette.action.disabled : undefined,
    cursor: 'grab',
    flexShrink: 0,
    visibility: cypressFlag ? 'visible' : 'hidden',
  },
  text: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(0.5),
    minWidth: 0,
  },
}));

type StyleProps = { disabled: boolean };

interface InputFieldButtonProps extends Omit<ButtonBaseProps, 'disabled'> {
  dataCy: string;
  dragField: DragField;
  LeftIcon: React.FC<{ disabled?: boolean }>;
  text: string;
  disabledReason?: string;
  dragDataCy?: string;
  helpText?: string;
}

export const InputFieldButton = forwardRef(function InputFieldButton(
  props: InputFieldButtonProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const {
    LeftIcon,
    className,
    dataCy,
    disabledReason = '',
    dragDataCy,
    dragField,
    helpText = '',
    text,
    ...rest
  } = props;
  const isDisabled = Boolean(disabledReason);
  const { classes } = useStyles({ disabled: isDisabled });
  const { attributes, listeners, setNodeRef } = useDraggable({
    data: dragField,
    id: dragField.nodeId,
  });
  const tooltipTitle = disabledReason || helpText;

  return (
    <AlineTooltip arrow placement="top" title={tooltipTitle}>
      <div style={{ minWidth: 0, width: '100%' }}>
        <ButtonBase
          ref={composeRefs<HTMLButtonElement>(ref, setNodeRef)}
          className={clsx(classes.button, className)}
          data-cy={dataCy}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
          {...rest}
        >
          <LeftIcon disabled={isDisabled} />
          <Typography noWrap align="left" className={classes.text} color="inherit" variant="body1">
            {text}
          </Typography>
          <DragIcon className={classes.dragIcon} data-cy={dragDataCy} />
        </ButtonBase>
      </div>
    </AlineTooltip>
  );
});
